import React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { F0Provider as F0p, dataCollectionLocalStorageHandler } from '@factorialco/f0-react'

import { EnvironmentContext } from 'config/environment'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

export const F0Provider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()

  const { renderDataTestIdAttribute, showExperimentalWarnings } =
    React.useContext(EnvironmentContext)

  const {
    access: { locale }
  } = useCurrentSession()

  return (
    <F0p
      privacyModeInitiallyEnabled
      l10n={{ l10n: { locale } }}
      i18n={{
        translations: {
          inputs: {
            password: {
              show: i18n.t('f0.inputs.password.show'),
              hide: i18n.t('f0.inputs.password.hide')
            }
          },
          select: {
            noResults: i18n.t('f0.select.no_results'),
            loadingMore: i18n.t('f0.select.loading_more'),
            applySelection: i18n.t('f0.select.apply_selection')
          },
          ai: {
            ask: i18n.t('f0.ai.ask'),
            resourcesGroupTitle: i18n.t('f0.ai.resources_group_title'),
            thinking: i18n.t('f0.ai.thinking'),
            feedbackModal: {
              positive: {
                title: i18n.t('common.ai.feedback_modal.positive.title'),
                placeholder: i18n.t('common.ai.feedback_modal.positive.placeholder'),
                label: i18n.t('common.ai.feedback_modal.positive.label')
              },
              negative: {
                title: i18n.t('common.ai.feedback_modal.negative.title'),
                placeholder: i18n.t('common.ai.feedback_modal.negative.placeholder'),
                label: i18n.t('common.ai.feedback_modal.negative.label')
              }
            },
            responseStopped: i18n.t('common.ai.response_stopped'),
            expandChat: i18n.t('common.ai.expand_chat'),
            collapseChat: i18n.t('common.ai.collapse_chat'),
            openChat: i18n.t('common.ai.open_chat'),
            scrollToBottom: i18n.t('common.ai.scroll_to_bottom'),
            startNewChat: i18n.t('common.ai.start_new_chat'),
            welcome: i18n.t('common.ai.welcome'),
            defaultInitialMessage: i18n.t('common.ai.initial'),
            closeChat: i18n.t('common.ai.close_chat'),
            inputPlaceholder: i18n.t('common.ai.input_placeholder'),
            stopAnswerGeneration: i18n.t('common.ai.stop_answer_generation'),
            sendMessage: i18n.t('common.ai.send_message'),
            thoughtsGroupTitle: i18n.t('common.ai.thoughts_group_title'),
            exportTable: i18n.t('common.ai.export_table'),
            generatedTableFilename: i18n.t('common.ai.generated_table_filename'),
            dataDownloadPreview: i18n.t('common.ai.data_download_preview'),
            viewProfile: i18n.t('common.ai.view_profile'),
            tools: i18n.t('common.ai.tools'),
            growth: {
              demoCard: {
                title: i18n.t('common.ai.growth.demo_card.title'),
                actionLabel: i18n.t('common.ai.growth.demo_card.action_label')
              },
              bookAMeetingCard: {
                title: i18n.t('common.ai.growth.book_a_meeting_card.title'),
                schedule: i18n.t('common.ai.growth.book_a_meeting_card.schedule'),
                actionLabel: i18n.t('common.ai.growth.book_a_meeting_card.action_label')
              },
              questionCard: {
                actionLabel: i18n.t('common.next'),
                skipLabel: i18n.t('common.ai.growth.question_card.skip_label'),
                sendLabel: i18n.t('common.send')
              },
              moduleCard: {
                actionLabel: i18n.t('common.ai.growth.module_card.action_label')
              },
              faqCard: {
                title: i18n.t('common.ai.growth.faq_card.title')
              }
            }
          },
          approvals: {
            history: i18n.t('common.approvals.history'),
            statuses: {
              waiting: i18n.t('common.approvals.statuses.waiting'),
              pending: i18n.t('common.approvals.statuses.pending'),
              approved: i18n.t('common.approvals.statuses.approved'),
              rejected: i18n.t('common.approvals.statuses.rejected')
            },
            requiredNumbers: {
              one: 'One approval required',
              other: '{{count}} approvals required'
            }
          },
          status: {
            selected: {
              singular: i18n.t('common.selected'),
              plural: i18n.t('common.selected_plural'),
              all: i18n.t('common.all_selected'),
              allOnPage: i18n.t('common.all_selected_on_page'),
              selectAllItems: i18n.t('common.select_all_items'),
              allItemsSelected: i18n.t('common.all_items_selected')
            },
            noItemsSelected: i18n.t('f0.select.no_items_selected')
          },
          navigation: {
            sidebar: {
              label: i18n.t('common.sidebar'),
              companySelector: {
                label: i18n.t('common.company_selector.label'),
                placeholder: i18n.t('common.company_selector.placeholder')
              }
            },
            previous: i18n.t('common.pagination.previous'),
            next: i18n.t('common.pagination.next')
          },
          actions: {
            toggleDropdownMenu: i18n.t('common.toggle_dropdown_menu'),
            save: i18n.t('common.save'),
            send: i18n.t('common.send'),
            cancel: i18n.t('common.cancel'),
            delete: i18n.t('common.delete'),
            paste: i18n.t('common.paste'),
            collapse: i18n.t('common.collapse'),
            expand: i18n.t('common.expand'),
            skipToContent: i18n.t('accessibility.skip_to_content'),
            showAll: i18n.t('common.show_all'),
            showLess: i18n.t('common.show_less'),
            view: i18n.t('common.view'),
            more: i18n.t('common.more'),
            clear: i18n.t('common.clear_selection'),
            search: i18n.t('common.search'),
            unselect: i18n.t('common.unselect'),
            copy: i18n.t('common.copy'),
            add: i18n.t('common.add'),
            edit: i18n.t('common.edit'),
            moveUp: i18n.t('common.moveUp'),
            moveDown: i18n.t('common.moveDown'),
            other: i18n.t('common.otherActions'),
            close: i18n.t('common.close'),
            toggle: i18n.t('common.toggle'),
            thumbsUp: i18n.t('common.thumbs_up'),
            thumbsDown: i18n.t('common.thumbs_down'),
            selectAll: i18n.t('common.select_all'),
            selectAllItems: i18n.t('common.select_all_items')
          },
          filters: {
            search: {
              relaxed: i18n.t('f0.filters.search.relaxed'),
              strict: i18n.t('f0.filters.search.strict')
            },
            searchPlaceholder: i18n.t('common.search_filter_placeholder'),
            inFilter: {
              searchPlaceholder: i18n.t('common.in_filter.search_placeholder')
            },
            activeFilters: i18n.t('common.active_filters'),
            filteringBy: i18n.t('common.filtering_by'),
            availableFilters: i18n.t('common.available_filters'),
            label: i18n.t('common.filters'),
            applyFilters: i18n.t('common.apply_filters'),
            applySelection: i18n.t('common.apply_selection'),
            cancel: i18n.t('common.cancel'),
            failedToLoadOptions: i18n.t('common.failed_to_load_options'),
            retry: i18n.t('common.retry'),
            number: {
              value: i18n.t('f0.filters.number.value'),
              equal: i18n.t('f0.filters.number.equal'),
              equalTo: i18n.t('f0.filters.number.equalTo'),
              lessOrEqual: i18n.t('f0.filters.number.lessOrEqual'),
              lessThan: i18n.t('f0.filters.number.lessThan'),
              greaterOrEqual: i18n.t('f0.filters.number.greaterOrEqual'),
              greaterThan: i18n.t('f0.filters.number.greaterThan'),
              equalShort: i18n.t('f0.filters.number.equalShort'),
              greaterThanOrEqualShort: i18n.t('f0.filters.number.greaterThanOrEqualShort'),
              greaterThanShort: i18n.t('f0.filters.number.greaterThanShort'),
              lessThanOrEqualShort: i18n.t('f0.filters.number.lessThanOrEqualShort'),
              lessThanShort: i18n.t('f0.filters.number.lessThanShort'),
              rangeTitle: i18n.t('f0.filters.number.rangeTitle'),
              range: i18n.t('f0.filters.number.range')
            },
            selectAll: i18n.t('f0.filters.select_all'),
            clear: i18n.t('f0.filters.clear')
          },
          toc: {
            search: i18n.t('f0.toc.search')
          },
          wizard: {
            previous: i18n.t('f0.wizard.previous'),
            next: i18n.t('f0.wizard.next'),
            submit: i18n.t('f0.wizard.submit'),
            stepOf: i18n.t('f0.wizard.step_of')
          },
          numberInput: {
            between: i18n.t('f0.numberInput.between'),
            greaterThan: i18n.t('f0.numberInput.greaterThan'),
            lessThan: i18n.t('f0.numberInput.lessThan')
          },
          coCreationForm: {
            actions: {
              actions: i18n.t('f0.co_creation_form.actions.actions'),
              duplicateQuestion: i18n.t('f0.co_creation_form.actions.duplicateQuestion'),
              deleteQuestion: i18n.t('f0.co_creation_form.actions.deleteQuestion'),
              duplicateSection: i18n.t('f0.co_creation_form.actions.duplicateSection'),
              deleteSection: i18n.t('f0.co_creation_form.actions.deleteSection')
            },
            questionTypes: {
              section: i18n.t('f0.co_creation_form.questionTypes.section'),
              rating: i18n.t('f0.co_creation_form.questionTypes.rating'),
              multipleChoice: i18n.t('f0.co_creation_form.questionTypes.multipleChoice'),
              singleChoice: i18n.t('f0.co_creation_form.questionTypes.singleChoice'),
              text: i18n.t('f0.co_creation_form.questionTypes.text'),
              longText: i18n.t('f0.co_creation_form.questionTypes.longText'),
              numeric: i18n.t('f0.co_creation_form.questionTypes.numeric'),
              link: i18n.t('f0.co_creation_form.questionTypes.link'),
              date: i18n.t('f0.co_creation_form.questionTypes.date')
            },
            selectQuestion: {
              addOption: i18n.t('f0.co_creation_form.selectQuestion.addOption'),
              newOption: i18n.t('f0.co_creation_form.selectQuestion.newOption'),
              markAsCorrect: i18n.t('f0.co_creation_form.selectQuestion.markAsCorrect'),
              remove: i18n.t('f0.co_creation_form.selectQuestion.remove'),
              correct: i18n.t('f0.co_creation_form.selectQuestion.correct'),
              optionPlaceholder: i18n.t('f0.co_creation_form.selectQuestion.optionPlaceholder')
            },
            answer: {
              label: i18n.t('f0.co_creation_form.answer.label'),
              placeholder: i18n.t('f0.co_creation_form.answer.placeholder')
            },
            labels: {
              applyingChanges: i18n.t('f0.co_creation_form.labels.applyingChanges'),
              endOfSection: i18n.t('f0.co_creation_form.labels.endOfSection'),
              title: i18n.t('f0.co_creation_form.labels.title'),
              titlePlaceholder: i18n.t('f0.co_creation_form.labels.titlePlaceholder'),
              description: i18n.t('f0.co_creation_form.labels.description'),
              questionDescriptionPlaceholder: i18n.t(
                'f0.co_creation_form.labels.questionDescriptionPlaceholder'
              ),
              sectionDescriptionPlaceholder: i18n.t(
                'f0.co_creation_form.labels.sectionDescriptionPlaceholder'
              ),
              required: i18n.t('f0.co_creation_form.labels.required'),
              questionType: i18n.t('f0.co_creation_form.labels.questionType'),
              questionOptions: i18n.t('f0.co_creation_form.labels.questionOptions'),
              actions: i18n.t('f0.co_creation_form.labels.actions'),
              sectionTitlePlaceholder: i18n.t('f0.co_creation_form.labels.sectionTitlePlaceholder')
            }
          },
          collections: {
            editableTable: {
              errors: {
                saveFailed: i18n.t('f0.collections.editable_table.errors.save_failed')
              }
            },
            visualizations: {
              settings: i18n.t('factorial_one.collections.visualizations.settings'),
              list: i18n.t('factorial_one.collections.visualizations.list_view'),
              table: i18n.t('factorial_one.collections.visualizations.table_view'),
              editableTable: i18n.t('factorial_one.collections.visualizations.editable_table_view'),
              card: i18n.t('factorial_one.collections.visualizations.card_view'),
              pagination: {
                of: i18n.t('factorial_one.collections.visualizations.pagination.of')
              },
              kanban: i18n.t('factorial_one.collections.visualizations.kanban_view'),
              reset: i18n.t('factorial_one.collections.visualizations.reset')
            },
            table: {
              settings: {
                showAllColumns: i18n.t('f0.collections.table.settings.show_all_columns'),
                hideAllColumns: i18n.t('f0.collections.table.settings.hide_all_columns')
              }
            },
            sorting: {
              noSorting: i18n.t('factorial_one.collections.sorting.no_sorting'),
              toggleDirection: i18n.t('factorial_one.collections.sorting.toggle_direction'),
              sortBy: i18n.t('factorial_one.collections.sorting.sort_by')
            },
            grouping: {
              noGrouping: i18n.t('factorial_one.collections.grouping.no_grouping'),
              groupBy: i18n.t('factorial_one.collections.grouping.group_by'),
              toggleDirection: i18n.t('factorial_one.collections.grouping.toggle_direction')
            },
            actions: {
              actions: i18n.t('factorial_one.collections.actions.actions')
            },
            emptyStates: {
              noData: {
                title: i18n.t('factorial_one.collections.empty_states.no_data.title'),
                description: i18n.t('factorial_one.collections.empty_states.no_data.description')
              },
              noResults: {
                title: i18n.t('factorial_one.collections.empty_states.no_results.title'),
                description: i18n.t(
                  'factorial_one.collections.empty_states.no_results.description'
                ),
                clearFilters: i18n.t(
                  'factorial_one.collections.empty_states.no_results.clear_filters'
                )
              },
              error: {
                title: i18n.t('factorial_one.collections.empty_states.error.title'),
                description: i18n.t('factorial_one.collections.empty_states.error.description'),
                retry: i18n.t('factorial_one.collections.empty_states.error.retry')
              }
            },
            itemsCount: i18n.t('factorial_one.collections.items_count'),
            summaries: {
              types: {
                sum: i18n.t('factorial_one.collections.summaries.types.sum')
              }
            }
          },
          date: {
            from: i18n.t('common.date.from'),
            to: i18n.t('common.date.to'),
            date: i18n.t('common.date.date'),
            custom: i18n.t('common.date.custom'),
            selectDate: i18n.t('common.date.select_date'),
            compareTo: i18n.t('common.date.compare_to'),
            none: i18n.t('common.date.none'),
            presets: {
              last7Days: i18n.t('common.date.presets.last_7_days'),
              last30Days: i18n.t('common.date.presets.last_30_days'),
              last3Months: i18n.t('common.date.presets.last_3_months'),
              last6Months: i18n.t('common.date.presets.last_6_months'),
              lastYear: i18n.t('common.date.presets.last_year'),
              last3Years: i18n.t('common.date.presets.last_3_years'),
              last100Years: i18n.t('common.date.presets.last_100_years')
            },
            range: 'Range',
            selectedBy: 'Selected by',
            granularities: {
              day: {
                currentDate: i18n.t('common.date.granularities.day.current_date'),
                label: i18n.t('common.date.granularities.day.label')
              },
              week: {
                currentDate: i18n.t('common.date.granularities.week.current_date'),
                label: i18n.t('common.date.granularities.week.label'),
                long: i18n.t('common.date.granularities.week.long_f0'),
                longSingular: i18n.t('common.date.granularities.week.long_singular'),
                longPlural: i18n.t('common.date.granularities.week.long_plural')
              },
              month: {
                currentDate: i18n.t('common.date.granularities.month.current_date'),
                label: i18n.t('common.date.granularities.month.label')
              },
              quarter: {
                currentDate: i18n.t('common.date.granularities.quarter.current_date'),
                label: i18n.t('common.date.granularities.quarter.label')
              },
              halfyear: {
                currentDate: i18n.t('common.date.granularities.halfyear.current_date'),
                label: i18n.t('common.date.granularities.halfyear.label')
              },
              year: {
                currentDate: i18n.t('common.date.granularities.year.current_date'),
                label: i18n.t('common.date.granularities.year.label')
              },
              range: {
                currentDate: i18n.t('common.date.granularities.range.current_date'),
                label: i18n.t('common.date.granularities.range.label')
              }
            },
            month: {
              january: i18n.t('common.month_names.january'),
              february: i18n.t('common.month_names.february'),
              march: i18n.t('common.month_names.march'),
              april: i18n.t('common.month_names.april'),
              may: i18n.t('common.month_names.may'),
              june: i18n.t('common.month_names.june'),
              july: i18n.t('common.month_names.july'),
              august: i18n.t('common.month_names.august'),
              september: i18n.t('common.month_names.september'),
              october: i18n.t('common.month_names.october'),
              november: i18n.t('common.month_names.november'),
              december: i18n.t('common.month_names.december')
            },
            groups: {
              today: i18n.t('common.date.groups.today'),
              yesterday: i18n.t('common.date.groups.yesterday'),
              lastWeek: i18n.t('common.date.groups.last_week'),
              lastMonth: i18n.t('common.date.groups.last_month'),
              other: i18n.t('common.date.groups.other')
            }
          },

          shortcut: i18n.t('common.shortcut'),
          notifications: i18n.t('common.notifications'),
          favorites: {
            favorites: i18n.t('common.favorites'),
            remove: i18n.t('common.remove')
          },
          imageUpload: {
            uploading: i18n.t('f0.image_upload.uploading'),
            uploadError: i18n.t('f0.image_upload.upload_error'),
            insertImage: i18n.t('f0.image_upload.insert_image'),
            deleteImage: i18n.t('f0.image_upload.delete_image'),
            errors: {
              fileTooLarge: i18n.t('f0.image_upload.errors.file_too_large'),
              invalidType: i18n.t('f0.image_upload.errors.invalid_type'),
              uploadFailed: i18n.t('f0.image_upload.errors.upload_failed'),
              dismiss: i18n.t('f0.image_upload.errors.dismiss')
            }
          },
          countries: {
            ad: i18n.t('countries.ad'),
            ae: i18n.t('countries.ae'),
            af: i18n.t('countries.af'),
            ag: i18n.t('countries.ag'),
            ai: i18n.t('countries.ai'),
            al: i18n.t('countries.al'),
            am: i18n.t('countries.am'),
            ao: i18n.t('countries.ao'),
            ar: i18n.t('countries.ar'),
            as: i18n.t('countries.as'),
            at: i18n.t('countries.at'),
            au: i18n.t('countries.au'),
            aw: i18n.t('countries.aw'),
            ax: i18n.t('countries.ax'),
            az: i18n.t('countries.az'),
            ba: i18n.t('countries.ba'),
            bb: i18n.t('countries.bb'),
            bd: i18n.t('countries.bd'),
            be: i18n.t('countries.be'),
            bf: i18n.t('countries.bf'),
            bg: i18n.t('countries.bg'),
            bh: i18n.t('countries.bh'),
            bi: i18n.t('countries.bi'),
            bj: i18n.t('countries.bj'),
            bm: i18n.t('countries.bm'),
            bo: i18n.t('countries.bo'),
            br: i18n.t('countries.br'),
            bt: i18n.t('countries.bt'),
            bw: i18n.t('countries.bw'),
            by: i18n.t('countries.by'),
            bz: i18n.t('countries.bz'),
            ca: i18n.t('countries.ca'),
            cd: i18n.t('countries.cd'),
            cf: i18n.t('countries.cf'),
            cg: i18n.t('countries.cg'),
            ch: i18n.t('countries.ch'),
            ci: i18n.t('countries.ci'),
            ck: i18n.t('countries.ck'),
            cl: i18n.t('countries.cl'),
            cm: i18n.t('countries.cm'),
            cn: i18n.t('countries.cn'),
            co: i18n.t('countries.co'),
            cr: i18n.t('countries.cr'),
            cu: i18n.t('countries.cu'),
            cv: i18n.t('countries.cv'),
            cw: i18n.t('countries.cw'),
            cy: i18n.t('countries.cy'),
            cz: i18n.t('countries.cz'),
            de: i18n.t('countries.de'),
            dj: i18n.t('countries.dj'),
            dk: i18n.t('countries.dk'),
            dm: i18n.t('countries.dm'),
            do: i18n.t('countries.do'),
            dz: i18n.t('countries.dz'),
            ec: i18n.t('countries.ec'),
            ee: i18n.t('countries.ee'),
            eg: i18n.t('countries.eg'),
            er: i18n.t('countries.er'),
            es: i18n.t('countries.es'),
            et: i18n.t('countries.et'),
            fi: i18n.t('countries.fi'),
            fj: i18n.t('countries.fj'),
            fk: i18n.t('countries.fk'),
            fm: i18n.t('countries.fm'),
            fo: i18n.t('countries.fo'),
            fr: i18n.t('countries.fr'),
            ga: i18n.t('countries.ga'),
            gb: i18n.t('countries.gb'),
            gd: i18n.t('countries.gd'),
            ge: i18n.t('countries.ge'),
            gg: i18n.t('countries.gg'),
            gh: i18n.t('countries.gh'),
            gi: i18n.t('countries.gi'),
            gl: i18n.t('countries.gl'),
            gm: i18n.t('countries.gm'),
            gn: i18n.t('countries.gn'),
            gq: i18n.t('countries.gq'),
            gr: i18n.t('countries.gr'),
            gt: i18n.t('countries.gt'),
            gu: i18n.t('countries.gu'),
            gw: i18n.t('countries.gw'),
            hk: i18n.t('countries.hk'),
            hn: i18n.t('countries.hn'),
            hr: i18n.t('countries.hr'),
            ht: i18n.t('countries.ht'),
            hu: i18n.t('countries.hu'),
            id: i18n.t('countries.id'),
            ie: i18n.t('countries.ie'),
            il: i18n.t('countries.il'),
            im: i18n.t('countries.im'),
            in: i18n.t('countries.in'),
            io: i18n.t('countries.io'),
            iq: i18n.t('countries.iq'),
            ir: i18n.t('countries.ir'),
            is: i18n.t('countries.is'),
            it: i18n.t('countries.it'),
            je: i18n.t('countries.je'),
            jm: i18n.t('countries.jm'),
            jo: i18n.t('countries.jo'),
            jp: i18n.t('countries.jp'),
            ke: i18n.t('countries.ke')
          },
          richTextEditor: {
            video: i18n.t('rich_text_editor.toolbar.video'),
            videoUrlPrompt: i18n.t('rich_text_editor.toolbar.videoUrlPrompt'),
            videoUrlInvalid: i18n.t('rich_text_editor.toolbar.videoUrlInvalid'),
            bold: i18n.t('rich_text_editor.toolbar.bold'),
            italic: i18n.t('rich_text_editor.toolbar.italic'),
            underline: i18n.t('rich_text_editor.toolbar.underline'),
            strike: i18n.t('rich_text_editor.toolbar.strike'),
            highlight: i18n.t('rich_text_editor.toolbar.highlight'),
            heading1: i18n.t('rich_text_editor.toolbar.heading1'),
            heading2: i18n.t('rich_text_editor.toolbar.heading2'),
            heading3: i18n.t('rich_text_editor.toolbar.heading3'),
            left: i18n.t('rich_text_editor.toolbar.left'),
            center: i18n.t('rich_text_editor.toolbar.center'),
            right: i18n.t('rich_text_editor.toolbar.right'),
            justify: i18n.t('rich_text_editor.toolbar.justify'),
            bulletList: i18n.t('rich_text_editor.toolbar.bulletList'),
            orderedList: i18n.t('rich_text_editor.toolbar.orderedList'),
            taskList: i18n.t('rich_text_editor.toolbar.taskList'),
            codeBlock: i18n.t('rich_text_editor.toolbar.codeBlock'),
            horizontalRule: i18n.t('rich_text_editor.toolbar.horizontalRule'),
            quote: i18n.t('rich_text_editor.toolbar.quote'),
            moreOptions: i18n.t('rich_text_editor.toolbar.moreOptions'),
            code: i18n.t('rich_text_editor.toolbar.code'),
            divider: i18n.t('rich_text_editor.toolbar.divider'),
            bullet: i18n.t('rich_text_editor.toolbar.bullet'),
            ordered: i18n.t('rich_text_editor.toolbar.ordered'),
            task: i18n.t('rich_text_editor.toolbar.task'),
            details: i18n.t('rich_text_editor.toolbar.dropdown'),
            link: i18n.t('rich_text_editor.toolbar.link'),
            linkPlaceholder: i18n.t('rich_text_editor.toolbar.linkPlaceholder'),

            groups: {
              textStyles: i18n.t('rich_text_editor.toolbar.groups.textStyles'),
              lists: i18n.t('rich_text_editor.toolbar.groups.lists'),
              blocks: i18n.t('rich_text_editor.toolbar.groups.blocks')
            },
            ai: {
              enhanceButtonLabel: i18n.t('rich_text_editor.ai.enhanceButtonLabel'),
              loadingEnhanceLabel: i18n.t('rich_text_editor.ai.loadingEnhanceLabel'),
              defaultError: i18n.t('rich_text_editor.ai.defaultError'),
              closeErrorButtonLabel: i18n.t('rich_text_editor.ai.closeErrorButtonLabel'),
              acceptChangesButtonLabel: i18n.t('rich_text_editor.ai.acceptChangesButtonLabel'),
              rejectChangesButtonLabel: i18n.t('rich_text_editor.ai.rejectChangesButtonLabel'),
              repeatButtonLabel: i18n.t('rich_text_editor.ai.repeatButtonLabel'),
              customPromptPlaceholder: i18n.t('rich_text_editor.ai.customPromptPlaceholder')
            }
          },
          forms: {
            actionBar: {
              saved: i18n.t('f0.forms.action_bar.saved'),
              saving: i18n.t('f0.forms.action_bar.saving'),
              unsavedChanges: i18n.t('f0.forms.action_bar.unsaved_changes'),
              discard: i18n.t('f0.forms.action_bar.discard'),
              issues: {
                one: i18n.t('f0.forms.action_bar.issues.one'),
                other: i18n.t('f0.forms.action_bar.issues.other')
              }
            },
            file: {
              dropzone: i18n.t('f0.forms.file.dropzone'),
              dropzoneActive: i18n.t('f0.forms.file.dropzone_active'),
              dropzoneMultiple: i18n.t('f0.forms.file.dropzone_multiple'),
              acceptedTypes: i18n.t('f0.forms.file.accepted_types'),
              remove: i18n.t('f0.forms.file.remove'),
              uploading: i18n.t('f0.forms.file.uploading'),
              processing: i18n.t('f0.forms.file.processing'),
              uploadFailed: i18n.t('f0.forms.file.upload_failed'),
              fileTooLarge: i18n.t('f0.forms.file.file_too_large'),
              invalidFileType: i18n.t('f0.forms.file.invalid_file_type')
            },
            validation: {
              required: i18n.t('f0.forms.validation.required'),
              invalidType: i18n.t('f0.forms.validation.invalid_type'),
              string: {
                email: i18n.t('f0.forms.validation.string.email'),
                url: i18n.t('f0.forms.validation.string.url'),
                min: i18n.t('f0.forms.validation.string.min'),
                max: i18n.t('f0.forms.validation.string.max')
              },
              number: {
                min: i18n.t('f0.forms.validation.number.min'),
                max: i18n.t('f0.forms.validation.number.max'),
                positive: i18n.t('f0.forms.validation.number.positive'),
                negative: i18n.t('f0.forms.validation.number.negative'),
                integer: i18n.t('f0.forms.validation.number.integer')
              },
              date: {
                min: i18n.t('f0.forms.validation.date.min'),
                max: i18n.t('f0.forms.validation.date.max'),
                invalid: i18n.t('f0.forms.validation.date.invalid')
              },
              array: {
                min: i18n.t('f0.forms.validation.array.min'),
                max: i18n.t('f0.forms.validation.array.max')
              },
              checkbox: {
                mustBeChecked: i18n.t('f0.forms.validation.checkbox.must_be_checked')
              }
            }
          }
        }
      }}
      link={{
        currentPath: location.pathname + location.search,
        component(props, ref) {
          const { href } = props

          if (href && href.startsWith('/')) {
            return <Link to={href} {...props} ref={ref} />
          }

          // eslint-disable-next-line factorial/no-raw-html
          return <a href={href} target="_blank" {...props} ref={ref} rel="noreferrer noopener" />
        }
      }}
      dataCollectionStorageHandler={dataCollectionLocalStorageHandler}
      renderDataTestIdAttribute={renderDataTestIdAttribute}
      showExperimentalWarnings={showExperimentalWarnings}
    >
      {children}
    </F0p>
  )
}
